/**
 * Admin Dashboard — stats overview + Message Board.
 *
 * Admin: can post / pin / edit / delete messages.
 * Resellers: read-only.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  getDashboardStats,
  adminMe,
  listMessages,
  createMessage,
  updateMessage,
  deleteMessage,
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Key,
  MonitorSmartphone,
  ListVideo,
  Bell,
  MessageSquare,
  Pin,
  Trash2,
  Send,
  PinOff,
} from "lucide-react";
import { ADMIN } from "../lib/testIds";

const StatCard = ({ icon: Icon, label, value, subValue, color, testId }) => (
  <Card data-testid={testId} className="border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-slate-500 font-medium">{label}</div>
          <div className="text-3xl font-bold mt-1 text-slate-900">{value}</div>
          {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PLATFORM_LABELS = {
  windows: "Windows",
  android: "Android",
  ios: "iOS",
  macos: "macOS",
  web: "Web",
  linux: "Linux",
  tv: "TV",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function MessageComposer({ onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim() || busy) return;
    setBusy(true);
    try {
      await createMessage({ title: title.trim(), body: body.trim(), pinned });
      setTitle("");
      setBody("");
      setPinned(false);
      onPosted?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="dashboard-message-composer"
      className="border border-slate-200 rounded-lg p-4 bg-slate-50/60 space-y-3"
    >
      <Input
        data-testid="dashboard-message-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Announcement title…"
        className="bg-white"
        maxLength={140}
      />
      <Textarea
        data-testid="dashboard-message-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Message body — visible to admins and resellers on their dashboards."
        className="bg-white min-h-[80px]"
        maxLength={2000}
      />
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <Switch
            data-testid="dashboard-message-pin-toggle"
            checked={pinned}
            onCheckedChange={setPinned}
          />
          <Pin className="w-3.5 h-3.5" /> Pin to top
        </label>
        <Button
          type="submit"
          data-testid="dashboard-message-submit"
          disabled={busy || !title.trim() || !body.trim()}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Send className="w-3.5 h-3.5 mr-1.5" />
          Post
        </Button>
      </div>
    </form>
  );
}

function MessageItem({ message, canEdit, onChanged }) {
  const [busy, setBusy] = useState(false);

  const togglePin = async () => {
    setBusy(true);
    try {
      await updateMessage(message.id, { pinned: !message.pinned });
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this message?")) return;
    setBusy(true);
    try {
      await deleteMessage(message.id);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`dashboard-message-${message.id}`}
      className={`border rounded-lg p-4 transition-colors ${
        message.pinned
          ? "border-orange-300 bg-orange-50/60"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {message.pinned && (
              <Pin className="w-3.5 h-3.5 text-orange-500 shrink-0" />
            )}
            <h4 className="text-sm font-semibold text-slate-900 truncate">
              {message.title}
            </h4>
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
            {message.body}
          </p>
          <div className="text-[11px] text-slate-400 mt-2">
            {message.author_username || "admin"} · {fmtDate(message.created_at)}
          </div>
        </div>
        {canEdit && (
          <div className="flex gap-1 shrink-0">
            <button
              data-testid={`dashboard-message-pin-${message.id}`}
              onClick={togglePin}
              disabled={busy}
              title={message.pinned ? "Unpin" : "Pin"}
              className="p-1.5 rounded-md text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-colors"
            >
              {message.pinned ? (
                <PinOff className="w-4 h-4" />
              ) : (
                <Pin className="w-4 h-4" />
              )}
            </button>
            <button
              data-testid={`dashboard-message-delete-${message.id}`}
              onClick={remove}
              disabled={busy}
              title="Delete"
              className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);

  const reloadMessages = () => {
    if (!isAdminRef.current) {
      setMessages([]);
      setLoadingMessages(false);
      return;
    }
    setLoadingMessages(true);
    listMessages()
      .then(setMessages)
      .catch(() => setMessages([]))
      .finally(() => setLoadingMessages(false));
  };

  // Hold the latest admin flag in a ref so reloadMessages — invoked by
  // child components after they post / pin / delete — always reads the
  // current value without forcing a re-render.
  const isAdminRef = useRef(false);

  useEffect(() => {
    getDashboardStats()
      .then(setStats)
      .finally(() => setLoading(false));
    adminMe()
      .then((u) => {
        setMe(u);
        isAdminRef.current = u?.role === "admin";
        reloadMessages();
      })
      .catch(() => {
        setLoadingMessages(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="text-slate-500">Loading...</div>;
  if (!stats) return <div className="text-slate-500">Failed to load stats</div>;

  const isAdmin = me?.role === "admin";

  return (
    <div className="space-y-6">
      <p className="text-slate-600">Overview of your ZA Player Pro deployment</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          testId={ADMIN.statKeys}
          icon={Key}
          label="Activation Keys"
          value={stats.keys.total}
          subValue={`${stats.keys.active} active`}
          color="bg-orange-500"
        />
        <StatCard
          testId={ADMIN.statDevices}
          icon={MonitorSmartphone}
          label="Devices"
          value={stats.devices.total}
          subValue={`${stats.devices.active} active`}
          color="bg-blue-500"
        />
        <StatCard
          testId={ADMIN.statPlaylists}
          icon={ListVideo}
          label="Playlists"
          value={stats.playlists}
          subValue="Provider playlists configured"
          color="bg-emerald-500"
        />
      </div>

      {/* === Message Board (admin only) === */}
      {isAdmin && (
        <Card data-testid="dashboard-message-board" className="border-slate-200">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-orange-500" />
              Message Board
            </CardTitle>
            <span className="text-xs text-slate-400">
              Internal admin notes — not visible to resellers
            </span>
          </CardHeader>
          <CardContent className="space-y-4">
            <MessageComposer onPosted={reloadMessages} />

            {loadingMessages ? (
              <p className="text-slate-500 text-sm">Loading messages…</p>
            ) : messages.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg">
                Post the first announcement above.
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <MessageItem
                    key={m.id}
                    message={m}
                    canEdit={true}
                    onChanged={reloadMessages}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Devices by Platform</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(stats.devices.by_platform || {}).length === 0 ? (
            <p className="text-slate-500 text-sm">No devices activated yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(stats.devices.by_platform).map(([platform, count]) => (
                <div
                  key={platform}
                  className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3"
                >
                  <div className="text-xs text-slate-500 uppercase tracking-wider">
                    {PLATFORM_LABELS[platform] || platform}
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mt-1">{count}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Notifications</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-slate-500" />
          <span className="text-slate-700">
            {stats.notifications} notifications sent
          </span>
        </CardContent>
      </Card>
    </div>
  );
}
